import {
	createAtomNameFromOptions,
	createSetAtomName,
	createSetTransformedAtomName,
	createSetValueAtomName,
	NameOptions,
} from "../extras/reactive-names";
import { afterChange, beforeChange } from "../extras/reactive-plugin";
import { createAtom } from "../primitives/reactive-atom";
import { Atom } from "../primitives/reactive-primitive-types";
import { ContextReactiveOptions } from "./reactive-abstraction-types";
import { getChild, getChildOptions } from "./reactive-children";
import { createProxy, ensureInert, getReactive } from "./reactive-proxy";
import {
	getSetAddChanges,
	getSetDeleteChanges,
	getSetClearChanges,
} from "../extras/reactive-plugin-set";

type Target = Set<unknown>;

type Reactive = {
	items: Atom;
};

export const createReactiveSet = <T extends Set<unknown>>(
	inertSet: T,
	reactiveOptions?: ContextReactiveOptions,
	nameOptions?: NameOptions
): T => {
	const atomName = createAtomNameFromOptions(nameOptions);

	const proxy = new Proxy(
		inertSet,
		new ReactiveSetProxyHandler(
			new ReactiveSetOverrides(
				atomName,
				inertSet,
				{
					items: createAtom(createSetAtomName(atomName)),
				},
				() => proxy,
				reactiveOptions
			)
		)
	) as T;

	return proxy;
};

class ReactiveSetProxyHandler implements ProxyHandler<Target> {
	private setOverrides: ReactiveSetOverrides;

	constructor(setOverrides: ReactiveSetOverrides) {
		this.setOverrides = setOverrides;
	}

	get(inertSet: Target, property: string | symbol, receiver: unknown) {
		const ownPropertyDescriptor = Reflect.getOwnPropertyDescriptor(
			inertSet,
			property
		);

		const isInherited =
			Reflect.has(inertSet, property) && !ownPropertyDescriptor;

		if (isInherited && property in this.setOverrides) {
			return this.setOverrides[property as keyof ReactiveSetOverrides];
		}

		return Reflect.get(inertSet, property, receiver);
	}
}

class ReactiveSetOverrides implements Set<unknown> {
	#atomName: string;
	#inertSet: Set<unknown>;
	#reactive: Reactive;
	#getProxy: () => Set<unknown>;
	#options: { reactiveOptions?: ContextReactiveOptions };

	constructor(
		atomName: string,
		inertSet: Set<unknown>,
		reactive: Reactive,
		getProxy: () => Set<unknown>,
		reactiveOptions?: ContextReactiveOptions
	) {
		this.#atomName = atomName;
		this.#inertSet = inertSet;
		this.#reactive = reactive;
		this.#getProxy = getProxy;
		this.#options = { reactiveOptions };
	}

	[Symbol.iterator] = () => {
		return this.#getSetValues();
	};

	entries = () => {
		return this.#entries();
	};

	keys = () => {
		return this.#getSetValues();
	};

	values = () => {
		return this.#getSetValues();
	};

	get [Symbol.toStringTag]() {
		return Set.prototype[Symbol.toStringTag];
	}

	get size(): number {
		this.#reactive.items.reportObserved();
		return this.#inertSet.size;
	}

	has = (value: unknown): boolean => {
		const result = this.#hasStoredValue(this.#getStored(value));
		this.#reactive.items.reportObserved();
		return result;
	};

	add = (value: unknown) => {
		const valueToStore = this.#getStored(value);
		const itemAlreadyExists = this.#hasStoredValue(valueToStore);

		const changes =
			!this.#hasPlugins() || itemAlreadyExists
				? null
				: getSetAddChanges(this.#getProxy(), valueToStore);

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		Set.prototype.add.call(this.#inertSet, valueToStore);
		if (!itemAlreadyExists) {
			this.#reactive.items.reportChanged();
		}

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}

		return this;
	};

	delete = (value: unknown): boolean => {
		const valueToDelete = this.#getStored(value);
		const itemAlreadyExists = this.#hasStoredValue(valueToDelete);

		const changes =
			!this.#hasPlugins() || !itemAlreadyExists
				? null
				: getSetDeleteChanges(this.#getProxy(), valueToDelete);

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		const resultStored = Set.prototype.delete.call(
			this.#inertSet,
			valueToDelete
		);
		const reactive = getReactive(valueToDelete as object);
		const resultReactive = !!(
			reactive && Set.prototype.delete.call(this.#inertSet, reactive)
		);
		const result = resultStored || resultReactive;
		if (result) {
			this.#reactive.items.reportChanged();
		}

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}

		return result;
	};

	clear = () => {
		const isAlreadyEmpty = this.#inertSet.size === 0;

		const changes =
			!this.#hasPlugins() || isAlreadyEmpty
				? null
				: getSetClearChanges(
						this.#getProxy(),
						Array.from(this.#inertSet.values())
				  );

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		Set.prototype.clear.call(this.#inertSet);
		if (!isAlreadyEmpty) {
			this.#reactive.items.reportChanged();
		}

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}
	};

	forEach = (
		callbackfn: (value: unknown, key: unknown, set: Set<unknown>) => void,
		thisArg?: any
	) => {
		try {
			const result = Set.prototype.forEach.call(
				this.#inertSet,
				(_value: unknown, key: unknown, set: Set<unknown>) => {
					const keyValue = getChild(this.#options, key, () =>
						createProxy(key, getChildOptions(this.#options), {
							parentAtomName: this.#atomName,
							createAtomName: createSetValueAtomName,
						})
					);
					return callbackfn.call(this, keyValue, keyValue, set);
				},
				thisArg
			);
			return result;
		} finally {
			this.#reactive.items.reportObserved();
		}
	};

	union = <U>(other: ReadonlySetLike<U>) => {
		try {
			return createProxy(
				this.#inertSet.union(other),
				getChildOptions(this.#options),
				{
					parentAtomName: this.#atomName,
					createAtomName: createSetTransformedAtomName,
				}
			);
		} finally {
			this.#reactive.items.reportObserved();
		}
	};

	intersection = <U>(other: ReadonlySetLike<U>) => {
		try {
			return createProxy(
				this.#inertSet.intersection(other),
				getChildOptions(this.#options),
				{
					parentAtomName: this.#atomName,
					createAtomName: createSetTransformedAtomName,
				}
			);
		} finally {
			this.#reactive.items.reportObserved();
		}
	};

	difference = <U>(other: ReadonlySetLike<U>) => {
		try {
			return createProxy(
				this.#inertSet.difference(other),
				getChildOptions(this.#options),
				{
					parentAtomName: this.#atomName,
					createAtomName: createSetTransformedAtomName,
				}
			);
		} finally {
			this.#reactive.items.reportObserved();
		}
	};

	symmetricDifference = <U>(other: ReadonlySetLike<U>) => {
		try {
			return createProxy(
				this.#inertSet.symmetricDifference(other),
				getChildOptions(this.#options),
				{
					parentAtomName: this.#atomName,
					createAtomName: createSetTransformedAtomName,
				}
			);
		} finally {
			this.#reactive.items.reportObserved();
		}
	};

	isSubsetOf = (other: ReadonlySetLike<unknown>) => {
		getReactive(other)?.size;
		try {
			return this.#inertSet.isSubsetOf(ensureInert(other));
		} finally {
			this.#reactive.items.reportObserved();
		}
	};

	isSupersetOf = (other: ReadonlySetLike<unknown>) => {
		getReactive(other)?.size;
		try {
			return this.#inertSet.isSupersetOf(ensureInert(other));
		} finally {
			this.#reactive.items.reportObserved();
		}
	};

	isDisjointFrom = (other: ReadonlySetLike<unknown>) => {
		getReactive(other)?.size;
		try {
			return this.#inertSet.isDisjointFrom(ensureInert(other));
		} finally {
			this.#reactive.items.reportObserved();
		}
	};

	*#entries(): SetIterator<[unknown, unknown]> {
		const result: SetIterator<[unknown, unknown]> = Set.prototype.entries.call(
			this.#inertSet
		);
		this.#reactive.items.reportObserved();
		for (const [key] of result) {
			const keyValue = getChild(this.#options, key, () =>
				createProxy(key, getChildOptions(this.#options), {
					parentAtomName: this.#atomName,
					createAtomName: createSetValueAtomName,
				})
			);
			yield [keyValue, keyValue];
		}
	}

	*#getSetValues(): SetIterator<unknown> {
		const result: SetIterator<unknown> = Set.prototype.values.call(
			this.#inertSet
		);
		this.#reactive.items.reportObserved();
		for (const item of result) {
			yield getChild(this.#options, item, () =>
				createProxy(item, getChildOptions(this.#options), {
					parentAtomName: this.#atomName,
					createAtomName: createSetValueAtomName,
				})
			);
		}
	}

	#getStored(value: unknown) {
		return this.#options.reactiveOptions?.deep === false
			? value
			: ensureInert(value);
	}

	#hasStoredValue(storedValue: unknown) {
		const hasStoredValueDirectly = Set.prototype.has.call(
			this.#inertSet,
			storedValue
		);
		if (
			hasStoredValueDirectly ||
			this.#options.reactiveOptions?.deep === false
		) {
			return hasStoredValueDirectly;
		}

		const reactive = getReactive(storedValue as object);
		return (
			(reactive && Set.prototype.has.call(this.#inertSet, reactive)) || false
		);
	}

	#hasPlugins() {
		return (this.#options.reactiveOptions?.plugins?.length ?? 0) > 0;
	}
}
